import {useEffect, useState} from 'react'
import clsx from 'clsx'
import {useFormik} from 'formik'
import moment from 'moment'
import {useNavigate, useParams} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import * as Yup from 'yup'
import {KTIcon} from '../../../_websoft/helpers'
import {
  fetchObradaSmjene,
  updateObradaSmjene,
} from '../../api/thermoboardNalozi'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import notifyToast from '../../components/Toast/Toast'

type ObradaPlocaFormValues = {
  vrijeme_pocetka: string
  vrijeme_zavrsetka: string
  pauza_minuta: string
  pauza_kvar: string
  pauza_kvar_odobreno: string
}

const initialValues: ObradaPlocaFormValues = {
  vrijeme_pocetka: '',
  vrijeme_zavrsetka: '',
  pauza_minuta: '',
  pauza_kvar: '',
  pauza_kvar_odobreno: '',
}

const toDateTimeLocal = (value?: string | null) => {
  if (!value || !moment(value).isValid()) {
    return ''
  }

  return moment(value).format('DD.MM.YYYY HH:mm')
}

const isValidDateTime = (value?: string) =>
  Boolean(value && moment(value, 'DD.MM.YYYY HH:mm', true).isValid())

const toDurationMinutes = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  if (typeof value === 'string' && value.includes(':')) {
    const [hours = '0', minutes = '0', seconds = '0'] = value.split(':')
    const totalMinutes =
      Number(hours) * 60 + Number(minutes) + Math.round(Number(seconds) / 60)

    return Number.isFinite(totalMinutes) ? totalMinutes.toString() : ''
  }

  const minutes = Number(value)

  return Number.isFinite(minutes) && minutes >= 0 ? Math.round(minutes).toString() : ''
}

const validationSchema = Yup.object({
  vrijeme_pocetka: Yup.string()
    .test(
      'valid-start-date-time',
      'Koristite format DD.MM.YYYY HH:mm',
      (value) => !value || isValidDateTime(value)
    )
    .required('Obavezno polje'),
  vrijeme_zavrsetka: Yup.string()
    .test(
      'valid-end-date-time',
      'Koristite format DD.MM.YYYY HH:mm',
      (value) => !value || isValidDateTime(value)
    )
    .test(
      'after-start',
      'Vrijeme završetka mora biti nakon početka',
      function (value) {
        const {vrijeme_pocetka} = this.parent

        if (!isValidDateTime(value) || !isValidDateTime(vrijeme_pocetka)) {
          return true
        }

        return moment(value, 'DD.MM.YYYY HH:mm', true).isSameOrAfter(
          moment(vrijeme_pocetka, 'DD.MM.YYYY HH:mm', true)
        )
      }
    )
    .required('Obavezno polje'),
  pauza_minuta: Yup.number()
    .typeError('Pauza mora biti cijeli broj minuta')
    .integer('Pauza mora biti cijeli broj minuta')
    .min(0, 'Pauza ne može biti negativna')
    .required('Obavezno polje'),
  pauza_kvar: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .typeError('Pauza zastoj mora biti cijeli broj minuta')
    .integer('Pauza zastoj mora biti cijeli broj minuta')
    .min(0, 'Pauza zastoj ne može biti negativna')
    .nullable()
    .notRequired(),
  pauza_kvar_odobreno: Yup.string().required('Obavezno polje'),
})

const ThermoboardObradaPlocaDetais: React.FC = () => {
  const {id} = useParams()
  const smjenaId = Number(id)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [formValues, setFormValues] = useState<ObradaPlocaFormValues>(initialValues)

  useEffect(() => {
    let mounted = true

    const loadSmjena = async () => {
      if (!smjenaId) {
        notifyToast(false, 'Neispravan ID smjene.')
        setLoading(false)
        return
      }

      try {
        const data = await fetchObradaSmjene(smjenaId)

        if (mounted) {
          setFormValues({
            vrijeme_pocetka: toDateTimeLocal(data?.vrijeme_pocetka),
            vrijeme_zavrsetka: toDateTimeLocal(data?.vrijeme_zavrsetka),
            pauza_minuta: toDurationMinutes(data?.pauza_minuta),
            pauza_kvar: toDurationMinutes(data?.pauza_kvar),
            pauza_kvar_odobreno:
              data?.pauza_kvar_odobreno === null ||
              data?.pauza_kvar_odobreno === undefined
                ? ''
                : Number(data.pauza_kvar_odobreno).toString(),
          })
        }
      } catch (error: any) {
        notifyToast(
          false,
          error?.response?.data?.message || error?.message || 'Smjena nije pronađena.'
        )
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    loadSmjena()

    return () => {
      mounted = false
    }
  }, [smjenaId])

  const formik = useFormik<ObradaPlocaFormValues>({
    initialValues: formValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, {setSubmitting}) => {
      try {
        const payload = {
          vrijeme_pocetka: moment(
            values.vrijeme_pocetka,
            'DD.MM.YYYY HH:mm',
            true
          ).format('YYYY-MM-DD HH:mm:ss'),
          vrijeme_zavrsetka: moment(
            values.vrijeme_zavrsetka,
            'DD.MM.YYYY HH:mm',
            true
          ).format('YYYY-MM-DD HH:mm:ss'),
          pauza_minuta: Number(values.pauza_minuta),
          pauza_kvar: values.pauza_kvar === '' ? null : Number(values.pauza_kvar),
          pauza_kvar_odobreno: Number(values.pauza_kvar_odobreno),
        }

        const response = await updateObradaSmjene(smjenaId, payload)
        const updatedRecord = response?.data ?? response

        if (
          response?.success === false ||
          (response?.error !== undefined && response.error !== 0)
        ) {
          notifyToast(false, response?.message || 'Izmjena smjene nije uspjela.')
          return
        }

        notifyToast(
          true,
          response?.message || updatedRecord?.message || 'Smjena je uspješno izmijenjena.'
        )
        setTimeout(() => navigate('/thermoboard/obrada-ploca'), 1200)
      } catch (error: any) {
        notifyToast(
          false,
          error?.response?.data?.message || error?.message || 'Izmjena smjene nije uspjela.'
        )
      } finally {
        setSubmitting(false)
      }
    },
  })

  const hasError = (field: keyof ObradaPlocaFormValues) =>
    Boolean(formik.touched[field] && formik.errors[field])

  const fieldClass = (field: keyof ObradaPlocaFormValues, select = false) =>
    clsx(
      select
        ? 'form-select form-select-lg form-select-solid'
        : 'form-control form-control-lg form-control-solid',
      {
        'is-invalid': hasError(field),
        'is-valid': formik.touched[field] && !formik.errors[field],
      }
    )

  const renderError = (field: keyof ObradaPlocaFormValues) =>
    hasError(field) ? <div className='text-danger mt-2'>{formik.errors[field]}</div> : null

  return (
    <div className='card shadow-sm mb-5 mb-xl-10'>
      <ToastContainer />

      <div className='card-header border-0 pt-6'>
        <div className='card-title flex-column align-items-start m-0'>
          <h1 className='fs-3 fw-bold text-gray-900 mb-1'>Izmjena obrade ploča</h1>
          <span className='text-muted fw-semibold fs-7'>Smjena #{smjenaId}</span>
        </div>
      </div>

      {loading ? (
        <LoadingComponent />
      ) : (
        <form onSubmit={formik.handleSubmit} noValidate>
          <div className='card-body border-top p-4 p-md-9'>
            <div className='row g-6'>
              <div className='col-12 col-lg-6'>
                <label className='form-label required fw-bold'>Vrijeme početka</label>
                <input
                  type='text'
                  inputMode='numeric'
                  placeholder='DD.MM.YYYY HH:mm'
                  {...formik.getFieldProps('vrijeme_pocetka')}
                  className={fieldClass('vrijeme_pocetka')}
                />
                {renderError('vrijeme_pocetka')}
              </div>

              <div className='col-12 col-lg-6'>
                <label className='form-label required fw-bold'>Vrijeme završetka</label>
                <input
                  type='text'
                  inputMode='numeric'
                  placeholder='DD.MM.YYYY HH:mm'
                  {...formik.getFieldProps('vrijeme_zavrsetka')}
                  className={fieldClass('vrijeme_zavrsetka')}
                />
                {renderError('vrijeme_zavrsetka')}
              </div>

              <div className='col-12 col-md-6 col-lg-4'>
                <label className='form-label required fw-bold'>Pauza</label>
                <input
                  type='number'
                  inputMode='numeric'
                  min='0'
                  step='1'
                  placeholder='Minute'
                  {...formik.getFieldProps('pauza_minuta')}
                  className={fieldClass('pauza_minuta')}
                />
                {renderError('pauza_minuta')}
              </div>

              <div className='col-12 col-md-6 col-lg-4'>
                <label className='form-label fw-bold'>Pauza zastoj</label>
                <input
                  type='number'
                  inputMode='numeric'
                  min='0'
                  step='1'
                  placeholder='Minute'
                  {...formik.getFieldProps('pauza_kvar')}
                  className={fieldClass('pauza_kvar')}
                />
                {renderError('pauza_kvar')}
              </div>

              <div className='col-12 col-lg-4'>
                <label className='form-label required fw-bold'>Pauza odobrena</label>
                <select
                  {...formik.getFieldProps('pauza_kvar_odobreno')}
                  className={fieldClass('pauza_kvar_odobreno', true)}
                >
                  <option value=''>Odaberi...</option>
                  <option value='1'>Da</option>
                  <option value='0'>Ne</option>
                </select>
                {renderError('pauza_kvar_odobreno')}
              </div>
            </div>
          </div>

          <div className='card-footer d-flex flex-column flex-sm-row justify-content-end gap-3 py-6 px-4 px-md-9'>
            <button
              type='button'
              className='btn btn-light fw-bold px-8'
              onClick={() => navigate('/thermoboard/obrada-ploca')}
              disabled={formik.isSubmitting}
            >
              Nazad
            </button>
            <button
              type='submit'
              className='btn btn-primary fw-bold px-10'
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' />
                  Snimanje...
                </>
              ) : (
                <>
                  <KTIcon iconName='check' className='fs-2' />
                  Potvrdi
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

export default ThermoboardObradaPlocaDetais
